import {ReactNode, useId, useState} from "react";
import {ChevronDown, Info} from "lucide-react";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip.tsx";
import {cn} from "@/lib/utils.ts";

export default function DetailSection({
    title,
    count,
    info,
    headerDetails,
    children,
    className,
}: {
    title: string;
    count?: number;
    info?: string;
    headerDetails?: ReactNode;
    children: ReactNode;
    className?: string;
}) {
    const [expanded, setExpanded] = useState(true);
    const contentId = useId();

    return (
        <section className={cn(className)}>
            <header className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-surface/20 px-4">
                <h3 className="text-base font-bold tracking-wide" style={{fontVariant: "small-caps"}}>
                    {title}
                </h3>
                {count != undefined &&
                    <span className="rounded-full bg-surface-overlay px-1.5 py-0.5 text-[0.65rem] leading-none text-muted-foreground"
                          aria-label={`${count} Einträge`}>
                        {count}
                    </span>}
                {info &&
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button type="button"
                                    className="rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
                                    aria-label={`Hinweise zu ${title}`}>
                                <Info aria-hidden="true" size={15}/>
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="max-w-80 text-sm">{info}</p>
                        </TooltipContent>
                    </Tooltip>}
                {headerDetails && <div className="ml-auto">{headerDetails}</div>}
                <button type="button"
                        className={cn(
                            "ml-auto rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring @min-[48rem]:hidden",
                            headerDetails && "ml-0",
                        )}
                        aria-controls={contentId}
                        aria-expanded={expanded}
                        aria-label={`${title} ${expanded ? "einklappen" : "ausklappen"}`}
                        onClick={() => setExpanded(current => !current)}>
                    <ChevronDown aria-hidden="true" size={16}
                                 className={cn("transition-transform", !expanded && "-rotate-90")}/>
                </button>
            </header>
            <div id={contentId} className={cn("px-4 pb-3", !expanded && "hidden @min-[48rem]:block")}>
                {children}
            </div>
        </section>
    );
}
